import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ClipboardList, Gauge, ThumbsUp, TrendingDown } from 'lucide-react';
import {
  PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, Tooltip,
} from 'recharts';

/* ═══════════════════════════════════════════════════════════════════════
   L'AFFICHAGE DES RÉSULTATS D'UN DIAGNOSTIC — un seul, partout

   Fichier IDENTIQUE dans Front-end/admin et Front-end/user (comme
   `src/etats/`) : changer l'un, recopier l'autre. Il sert les résultats
   du diagnostic public (visiteur et administration), la lecture d'un
   diagnostic d'accompagnement (coach) et la page Diagnostic de
   l'organisation.

   `TableauResultats` pose l'ordre de lecture, le même partout :
     1. Équilibre stratégique, en bande : le radar comparé à la moyenne des
        structures, le score avec sa catégorie et son niveau, le baromètre,
        la restitution narrative ;
     2. forces et faiblesses, côte à côte, à parts égales ;
     3. le score par domaine, une couleur par niveau du barème ;
     4. le plan d'actions correctives, tout affiché — sauf côté public, où
        il est le contenu du rapport payant.
   ═══════════════════════════════════════════════════════════════════════ */

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Le barème officiel (`accompagnement.interpretation.BAREME`) :
// (borne haute en %, niveau, catégorie, professionnalisation, couleur de
// la jauge, couleur du texte — lisible sur fond clair).
const BAREME = [
  [20, 'Très faible', 'C', 'Pas professionnelle', '#dc2626', '#b91c1c'],
  [49, 'Faible', 'B', 'Un peu professionnelle', '#f97316', '#c2410c'],
  [70, 'Moyen', 'B+', 'Moyennement professionnelle', '#eab308', '#a16207'],
  [90, 'Bon', 'A', 'Professionnelle', '#5EBD4F', '#2f7d24'],
  [100, 'Acquis', 'A+', 'Très professionnelle', '#15803d', '#166534'],
];

const borne = (v) => Math.max(0, Math.min(100, Number(v) || 0));
const pourcent = (v) => `${String(Math.round(Number(v) * 10) / 10).replace('.', ',')} %`;

export function niveauDuScore(score) {
  const s = borne(score);
  const [, niveau, categorie, professionnalisation, jauge, texte] =
    BAREME.find(([b]) => s <= b) || BAREME[BAREME.length - 1];
  return { niveau, categorie, professionnalisation, jauge, texte };
}

/* La moyenne des structures diagnostiquées, par domaine : la référence du
   radar. Un agrégat public, chargé une fois pour toute la page ; s'il
   manque (serveur injoignable, trop peu de diagnostics), le radar reste
   affiché sans comparaison. */
let moyennesEnCours = null;
export function useMoyennesDomaines() {
  const [moyennes, setMoyennes] = useState(null);
  useEffect(() => {
    let vivant = true;
    if (!moyennesEnCours) {
      moyennesEnCours = fetch(`${API}/diagnostics/moyennes-domaines/`)
        .then((r) => (r.ok ? r.json() : { domaines: [] }))
        .then((d) => Object.fromEntries((d?.domaines || []).map((l) => [l.domaine, l.score])))
        .catch(() => { moyennesEnCours = null; return {}; });
    }
    moyennesEnCours.then((t) => vivant && setMoyennes(t));
    return () => { vivant = false; };
  }, []);
  return moyennes;
}

export function Jauge({ score, repere = null, epaisseur = 'h-2' }) {
  const valeur = borne(score);
  return (
    <div className={`relative ${epaisseur} rounded-full bg-[var(--bg-main)]`} aria-hidden="true">
      <div className="h-full rounded-full transition-[width] duration-500"
           style={{ width: `${valeur}%`, backgroundColor: niveauDuScore(valeur).jauge }} />
      {repere != null && (
        <span className="absolute -top-1 -bottom-1 w-0.5 rounded-full bg-[var(--text-muted)]"
              style={{ left: `calc(${borne(repere)}% - 1px)` }} />
      )}
    </div>
  );
}

/* Un domaine : son nom, son score et son niveau, la jauge — et, s'il y en
   a, le détail qui se déplie (lecture du coach, sous-domaines). */
export function LigneDomaine({ nom, score, niveau, moyenne = null, precision, children }) {
  const [ouvert, setOuvert] = useState(false);
  const n = niveauDuScore(score);
  const entete = (
    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1.5">
        <span className="font-semibold text-sm text-[var(--text-bold)]">{nom}</span>
        <span className="text-xs tabular-nums font-semibold" style={{ color: n.texte }}>
          {score == null ? '—' : pourcent(score)} · {niveau || n.niveau}
          {moyenne != null && <span className="font-normal text-[var(--text-muted)]"> · moyenne {pourcent(moyenne)}</span>}
          {precision && <span className="font-normal text-[var(--text-muted)]"> · {precision}</span>}
        </span>
      </div>
      <Jauge score={score} repere={moyenne} />
    </div>
  );
  if (!children) {
    return <li className="border border-[var(--border)] rounded-pastille px-4 py-3">{entete}</li>;
  }
  return (
    <li className="border border-[var(--border)] rounded-pastille">
      <button type="button" onClick={() => setOuvert((o) => !o)} aria-expanded={ouvert}
              className="w-full text-left px-4 py-3 flex items-center gap-3">
        {entete}
        <ChevronDown className={`w-4 h-4 shrink-0 text-[var(--text-muted)] transition-transform ${ouvert ? 'rotate-180' : ''}`} />
      </button>
      {ouvert && <div className="px-4 pb-4">{children}</div>}
    </li>
  );
}

/* Forces, faiblesses : une hauteur fixe, le contenu défile à l'intérieur. */
export function CarteListe({ titre, icone: Icone, teinte = 'var(--accent-texte)', items, vide, hauteur = 'h-80' }) {
  return (
    <section className={`bg-[var(--bg-card)] rounded-carte shadow-carte flex flex-col overflow-hidden ${hauteur}`}>
      <h3 className="text-sm font-semibold px-5 pt-5 pb-3 flex items-center gap-2 shrink-0" style={{ color: teinte }}>
        {Icone && <Icone className="w-4 h-4" />}{titre}
        {items?.length > 0 && <span className="ml-auto text-[11px] font-semibold text-[var(--text-muted)] tabular-nums">{items.length}</span>}
      </h3>
      <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5">
        {!items?.length ? <p className="text-sm text-[var(--text-muted)]">{vide}</p> : (
          <ul className="space-y-3">
            {items.map((it, i) => (
              <li key={`${it.titre}-${i}`} className="text-sm leading-relaxed">
                {it.titre && <span className="block font-semibold text-[var(--text-bold)]">{it.titre}</span>}
                <span className="text-[var(--text-main)] whitespace-pre-line">{it.description}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

// Deux échelles cohabitent : celle du plan d'accompagnement (critique →
// utile) et celle, plus ancienne, du diagnostic public (haute → basse).
// L'ordre des clés est l'ordre d'affichage.
const PRIORITES = {
  critique: { libelle: 'Critique', classe: 'text-[#b91c1c] bg-[#fef2f2] border-[#fecaca]' },
  haute: { libelle: 'Haute', classe: 'text-[#b91c1c] bg-[#fef2f2] border-[#fecaca]' },
  urgent: { libelle: 'Urgent', classe: 'text-[#c2410c] bg-[#fff7ed] border-[#fed7aa]' },
  important: { libelle: 'Important', classe: 'text-[#1b1a65] bg-[#eef2ff] border-[#c7d2fe]' },
  moyenne: { libelle: 'Moyenne', classe: 'text-[#a16207] bg-[#fefce8] border-[#fde68a]' },
  utile: { libelle: 'Utile', classe: 'text-[#475569] bg-[#f8fafc] border-[#e2e8f0]' },
  basse: { libelle: 'Basse', classe: 'text-[#2f7d24] bg-[#f0fdf4] border-[#bbf7d0]' },
};

// « Urgente », « Haute », « haute » : plusieurs façons d'écrire une priorité.
export function prioriteNormalisee(p) {
  const v = String(p || '').trim().toLowerCase();
  if (PRIORITES[v]) return v;
  if (v === 'urgente') return 'urgent';
  if (v === 'faible') return 'basse';
  return 'moyenne';
}

/* Le plan d'actions correctives, tout affiché : groupé par priorité, de la
   plus pressante à la moins pressante, chaque action avec ses livrables.
   `actions` : [{ description, priorite, domaine?, sous_domaine?, niveau?,
   livrables?, mention? }]. */
export function PlanCorrectif({ actions, vide = 'Aucune action corrective.' }) {
  // Les filtres trient ce qui s'affiche ; chaque action reste dépliée.
  const [domaine, setDomaine] = useState('');
  const [priorite, setPriorite] = useState('');
  const domaines = useMemo(
    () => [...new Set((actions || []).map((a) => a.domaine).filter(Boolean))], [actions]);
  const presentes = useMemo(
    () => Object.keys(PRIORITES).filter((cle) => (actions || []).some((a) => prioriteNormalisee(a.priorite) === cle)),
    [actions]);
  const filtrees = useMemo(() => (actions || [])
    .filter((a) => !domaine || a.domaine === domaine)
    .filter((a) => !priorite || prioriteNormalisee(a.priorite) === priorite), [actions, domaine, priorite]);
  const groupes = useMemo(() => Object.keys(PRIORITES)
    .map((cle) => ({ cle, actions: filtrees.filter((a) => prioriteNormalisee(a.priorite) === cle) }))
    .filter((g) => g.actions.length > 0), [filtrees]);

  if (!actions?.length) return <p className="text-sm text-[var(--text-muted)]">{vide}</p>;
  let rang = 0;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        {domaines.length > 1 && (
          <select value={domaine} onChange={(e) => setDomaine(e.target.value)} aria-label="Filtrer par domaine"
                  className="filtre min-w-[12rem]">
            <option value="">Tous les domaines</option>
            {domaines.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        )}
        {presentes.length > 1 && (
          <select value={priorite} onChange={(e) => setPriorite(e.target.value)} aria-label="Filtrer par priorité"
                  className="filtre min-w-[10rem]">
            <option value="">Toutes les priorités</option>
            {presentes.map((cle) => <option key={cle} value={cle}>{PRIORITES[cle].libelle}</option>)}
          </select>
        )}
        <span className="text-xs text-[var(--text-muted)] tabular-nums">
          {filtrees.length} action{filtrees.length > 1 ? 's' : ''}
        </span>
      </div>
      {groupes.length === 0 && (
        <p className="text-sm text-[var(--text-muted)]">Aucune action pour ces filtres.</p>
      )}
      {groupes.map((g) => (
        <section key={g.cle}>
          <h4 className="flex items-center gap-2 mb-3">
            <span className={`inline-flex px-2.5 py-0.5 rounded-full border text-[11px] font-semibold ${PRIORITES[g.cle].classe}`}>
              {PRIORITES[g.cle].libelle}
            </span>
            <span className="text-xs text-[var(--text-muted)] tabular-nums">
              {g.actions.length} action{g.actions.length > 1 ? 's' : ''}
            </span>
          </h4>
          <ol className="space-y-3">
            {g.actions.map((a) => {
              rang += 1;
              return (
                <li key={`${g.cle}-${rang}`} className="border border-[var(--border)] rounded-pastille px-4 py-3 flex gap-3">
                  <span className="shrink-0 w-6 text-[12px] font-semibold text-[var(--text-muted)] tabular-nums mt-0.5">{rang}</span>
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <p className="text-xs text-[var(--text-muted)]">
                      {[a.domaine, a.sous_domaine].filter(Boolean).join(' · ')}
                      {a.niveau ? ` · niveau ${a.niveau} / 5` : ''}
                      {a.mention && <span className="text-[var(--attention)]"> · {a.mention}</span>}
                    </p>
                    <p className="text-sm text-[var(--text-bold)] whitespace-pre-line leading-relaxed">{a.description}</p>
                    {a.livrables && (
                      <p className="text-xs text-[var(--text-muted)] whitespace-pre-line leading-relaxed">
                        <span className="font-semibold">Livrables attendus :</span>{'\n'}{a.livrables}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </section>
      ))}
    </div>
  );
}

/* La restitution d'un diagnostic public : la même phrase pour le visiteur
   et pour l'administration, tirée du barème. */
export function restitutionDuScore(nom, score) {
  const s = Math.round(borne(score));
  const n = niveauDuScore(s);
  const conclusion = s >= 75
    ? "Votre structure fait preuve d'une excellence organisationnelle remarquable."
    : s >= 50
      ? "Les bases d'une organisation solide sont présentes."
      : 'Le diagnostic révèle des chantiers de structuration prioritaires.';
  return `L'organisation ${nom || 'diagnostiquée'} affiche une maturité globale de ${s} % : `
    + `niveau « ${n.niveau} », ${n.professionnalisation.toLowerCase()} (catégorie ${n.categorie}). ${conclusion}`;
}

/* ─── Équilibre stratégique ─────────────────────────────────────────── */

/** Un axe de radar ne supporte pas les libellés longs : on coupe proprement. */
function AxeDomaine({ payload, x, y, textAnchor, longueur = 16, ...reste }) {
  const texte = String(payload?.value ?? '');
  const court = texte.length > longueur ? `${texte.slice(0, longueur - 1)}…` : texte;
  return (
    <text {...reste} x={x} y={y} textAnchor={textAnchor} fill="var(--text-muted)" fontSize={10.5} fontWeight={600}>
      <title>{texte}</title>
      {court}
    </text>
  );
}

function InfoBulle({ active, payload }) {
  const p = active && payload?.[0]?.payload;
  if (!p) return null;
  return (
    <div className="rounded-pastille bg-[var(--bg-card)] shadow-carte px-3 py-2 text-[12px] max-w-[220px]">
      <p className="font-semibold text-[var(--text-bold)] mb-1">{p.nom}</p>
      <p className="tabular-nums text-[var(--text-main)]">Score : <b>{pourcent(p.score)}</b></p>
      {p.moyenne != null && <p className="tabular-nums text-[var(--text-muted)]">Moyenne des structures : {pourcent(p.moyenne)}</p>}
    </div>
  );
}

function RadarEquilibre({ points }) {
  // La taille relevée sur le conteneur : en pourcentages, le tracé lisait
  // une largeur de 0 au montage et n'y revenait jamais.
  const boite = useRef(null);
  const [taille, setTaille] = useState({ l: 0, h: 0 });
  useLayoutEffect(() => {
    const el = boite.current;
    if (!el) return undefined;
    const relever = () => {
      const r = el.getBoundingClientRect();
      setTaille((t) => (Math.abs(t.l - r.width) > 1 || Math.abs(t.h - r.height) > 1
        ? { l: Math.round(r.width), h: Math.round(r.height) } : t));
    };
    const ro = new ResizeObserver(relever);
    ro.observe(el);
    relever();
    return () => ro.disconnect();
  }, []);

  const avecMoyenne = points.some((p) => p.moyenne != null);
  const compares = points.filter((p) => p.moyenne != null);
  const devant = compares.filter((p) => p.score >= p.moyenne).length;

  return (
    <div className="flex flex-col h-full">
      <div ref={boite} className="h-[240px]">
        {taille.l > 0 && points.length >= 3 && (
          // Étroit (téléphone), le radar rétrécit et les libellés raccourcissent :
          // sinon ils sortent de la carte.
          <RadarChart width={taille.l} height={taille.h} cx="50%" cy="50%"
                      outerRadius={taille.l < 420 ? '52%' : '66%'} data={points}>
            <PolarGrid stroke="var(--border)" />
            <PolarAngleAxis dataKey="nom" tick={<AxeDomaine longueur={taille.l < 420 ? 12 : 16} />} />
            <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
            {avecMoyenne && (
              <Radar name="Moyenne des structures" dataKey="moyenne" stroke="var(--text-muted)"
                     strokeDasharray="4 3" strokeWidth={1.5} fill="var(--text-muted)" fillOpacity={0.08}
                     isAnimationActive={false} />
            )}
            <Radar name="Votre organisation" dataKey="score" stroke="#5EBD4F" strokeWidth={2}
                   fill="#5EBD4F" fillOpacity={0.25} isAnimationActive={false} />
            <Tooltip content={<InfoBulle />} />
          </RadarChart>
        )}
        {points.length > 0 && points.length < 3 && (
          <p className="h-full grid place-items-center text-xs text-[var(--text-muted)] text-center px-4">
            Le radar se dessine à partir de trois domaines.
          </p>
        )}
      </div>
      <ul className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-[11.5px] text-[var(--text-main)] mt-1">
        <li className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-[#5EBD4F]/40 border-2 border-[#5EBD4F]" aria-hidden="true" />
          Votre organisation
        </li>
        {avecMoyenne && (
          <li className="inline-flex items-center gap-1.5">
            <span className="w-3 h-0 border-t-2 border-dashed border-[var(--text-muted)]" aria-hidden="true" />
            Moyenne des structures
          </li>
        )}
      </ul>
      <p className="text-[12.5px] text-[var(--text-muted)] text-center mt-2">
        {avecMoyenne ? (
          <>Au-dessus de la moyenne sur{' '}
            <span className="font-semibold text-[var(--text-bold)] tabular-nums">
              {devant} domaine{devant > 1 ? 's' : ''} sur {compares.length}
            </span>.</>
        ) : 'La comparaison avec les autres structures apparaîtra quand assez de diagnostics auront été menés.'}
      </p>
    </div>
  );
}

/* Le baromètre : un demi-cadran découpé selon le barème, et l'aiguille sur
   le score. */
export function Barometre({ score }) {
  // Sans score (aucun résultat encore), le cadran reste sans aiguille.
  const sansAiguille = score == null;
  const valeur = borne(score);
  const R = 80;
  const C = { x: 100, y: 96 };
  const point = (pct, r = R) => {
    const a = Math.PI * (1 - pct / 100);
    return [C.x + r * Math.cos(a), C.y - r * Math.sin(a)];
  };
  const arc = (de, a) => {
    const [x1, y1] = point(de);
    const [x2, y2] = point(a);
    return `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${R} ${R} 0 0 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;
  };
  const [ax, ay] = point(valeur, R - 18);
  let debut = 0;
  return (
    <svg viewBox="0 0 200 112" className="w-full max-w-[200px]" role="img"
         aria-label={sansAiguille ? 'Baromètre : aucun résultat' : `Baromètre : ${pourcent(valeur)}, niveau ${niveauDuScore(valeur).niveau}`}>
      {BAREME.map(([fin, niveau, , , couleur]) => {
        const d = arc(debut + (debut ? 0.8 : 0), fin);
        debut = fin;
        return <path key={niveau} d={d} stroke={couleur} strokeWidth="14" fill="none" />;
      })}
      {!sansAiguille && (
        <>
          <line x1={C.x} y1={C.y} x2={ax} y2={ay} stroke="var(--text-bold)" strokeWidth="3" strokeLinecap="round" />
          <circle cx={C.x} cy={C.y} r="6" fill="var(--text-bold)" />
        </>
      )}
      <text x="16" y="110" fontSize="9" fill="var(--text-muted)">0</text>
      <text x="178" y="110" fontSize="9" fill="var(--text-muted)">100</text>
    </svg>
  );
}

function BandeEquilibre({ score, domaines, moyennes, restitution, vide }) {
  const n = niveauDuScore(score);
  const points = domaines.map((d) => ({
    nom: d.nom,
    score: Math.round(borne(d.score) * 10) / 10,
    moyenne: moyennes?.[d.nom] ?? null,
  }));
  return (
    <section className="bg-[var(--bg-card)] rounded-carte shadow-carte p-5 sm:p-6">
      <h2 className="titre-carte mb-4">Équilibre stratégique</h2>
      <div className="grid gap-6 lg:grid-cols-12 items-center">
        <div className="lg:col-span-4">
          {vide ? (
            <div className="h-[240px] grid place-items-center rounded-pastille border border-dashed border-[var(--border)] text-sm text-[var(--text-muted)] text-center px-6">
              {vide}
            </div>
          ) : <RadarEquilibre points={points} />}
        </div>

        <div className="lg:col-span-2 text-center lg:border-l border-[var(--border)] lg:pl-4">
          <p className="text-[12px] text-[var(--text-muted)] mb-1">Score global</p>
          <p className="leading-none">
            <span className="text-5xl font-semibold text-[var(--text-bold)] tabular-nums">
              {vide ? '—' : Math.round(borne(score))}
            </span>
            <span className="text-lg font-semibold text-[var(--accent-texte)]">/100</span>
          </p>
          {!vide && (
            <div className="mt-3 flex flex-col items-center gap-1.5">
              <span className="px-2.5 py-1 rounded-pastille text-[12px] font-semibold border"
                    style={{ color: n.texte, borderColor: n.jauge }}>
                Catégorie {n.categorie}
              </span>
              <span className="text-[12.5px] font-semibold" style={{ color: n.texte }}>Niveau : {n.niveau}</span>
              <span className="text-[11.5px] text-[var(--text-muted)]">{n.professionnalisation}</span>
            </div>
          )}
        </div>

        <div className="lg:col-span-2 flex flex-col items-center lg:border-l border-[var(--border)] lg:pl-4">
          <p className="text-[12px] text-[var(--text-muted)] mb-2 inline-flex items-center gap-1.5">
            <Gauge className="w-3.5 h-3.5" /> Baromètre
          </p>
          <Barometre score={vide ? null : score} />
        </div>

        <div className="lg:col-span-4 lg:border-l border-[var(--border)] lg:pl-6">
          <h3 className="text-sm font-semibold text-[var(--text-bold)] mb-2">Restitution narrative</h3>
          <div className="text-[14px] text-[var(--text-main)] leading-relaxed space-y-2">
            {vide
              ? <p className="text-[var(--text-muted)]">La lecture des résultats s'affichera ici.</p>
              : (restitution || <p className="text-[var(--text-muted)]">Aucune analyse textuelle n'accompagne ce diagnostic.</p>)}
          </div>
        </div>
      </div>
    </section>
  );
}

/* La page de résultats, dans l'ordre de lecture convenu.

   - `domaines` : [{ nom, score, niveau? }] ;
   - `forces`, `faiblesses` : [{ titre?, description }] ;
   - `actions` : le plan correctif, ou `null` pour ne pas l'afficher
     (diagnostic public : c'est le rapport payant) ;
   - `vide` : un message — aucun résultat encore ; les cartes restent en
     place, vides, et disent pourquoi ;
   - `detailDomaine(d)` : un détail dépliable par domaine (lecture du coach) ;
   - `apres` : des cartes propres à la page, sous les scores par domaine. */
export function TableauResultats({
  score, domaines = [], restitution, forces = [], faiblesses = [], actions = null,
  vide = '', detailDomaine, apres, introPlan, videPlan,
}) {
  const moyennes = useMoyennesDomaines();
  return (
    <div className="space-y-6">
      <BandeEquilibre score={score} domaines={domaines} moyennes={moyennes} restitution={restitution} vide={vide} />

      <div className="grid gap-6 md:grid-cols-2">
        <CarteListe titre="Forces" icone={ThumbsUp} teinte="var(--accent-texte)"
                    vide={vide || 'Aucun sous-domaine au niveau 4 ou 5.'} items={vide ? [] : forces} />
        <CarteListe titre="Faiblesses" icone={TrendingDown} teinte="var(--alerte)"
                    vide={vide || 'Aucun point bloquant relevé.'} items={vide ? [] : faiblesses} />
      </div>

      <section className="bg-[var(--bg-card)] rounded-carte shadow-carte p-5 sm:p-6">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
          <h2 className="titre-carte">Score par domaine</h2>
          <ul className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[var(--text-muted)]" aria-label="Barème">
            {BAREME.map(([b, niveau, , , couleur], i) => (
              <li key={niveau} className="inline-flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: couleur }} aria-hidden="true" />
                {niveau} ({i ? BAREME[i - 1][0] + 1 : 0}–{b} %)
              </li>
            ))}
          </ul>
        </div>
        {vide || !domaines.length ? (
          <p className="text-sm text-[var(--text-muted)]">{vide || 'Aucun score par domaine.'}</p>
        ) : (
          <ul className="space-y-2">
            {domaines.map((d) => (
              <LigneDomaine key={d.nom} nom={d.nom} score={d.score} niveau={d.niveau}
                            moyenne={moyennes?.[d.nom] ?? null} precision={d.precision}>
                {detailDomaine?.(d)}
              </LigneDomaine>
            ))}
          </ul>
        )}
      </section>

      {apres}

      {actions !== null && (
        <section className="bg-[var(--bg-card)] rounded-carte shadow-carte p-5 sm:p-6">
          <h2 className="titre-carte flex items-center gap-2 mb-1">
            <ClipboardList className="w-5 h-5 text-[var(--accent-texte)]" /> Plan d'actions correctives
          </h2>
          {introPlan && <p className="text-xs text-[var(--text-muted)] mb-4">{introPlan}</p>}
          <div className={introPlan ? '' : 'mt-4'}>
            {vide
              ? <p className="text-sm text-[var(--text-muted)]">{vide}</p>
              : <PlanCorrectif actions={actions} vide={videPlan} />}
          </div>
        </section>
      )}
    </div>
  );
}
